import { Controller } from "@hotwired/stimulus"

// Drives the bulk-operation form: the operation picker reveals one
// operation section at a time, and within a section sub-pickers reveal
// their inputs (team reassignment's action → from/to users). Hidden inputs
// are disabled so they never submit alongside the active operation's params.
//
// Mass update shows every eligible field at once, like the tasks bulk edit:
// blank means leave unchanged, and this controller highlights the filled
// ones and restates them as a sentence before you preview.
export default class extends Controller {
  // actionRadio / actionBlock / choiceRadio are the generic guided-cards
  // machinery (owners, assignees): the checked card reveals its blocks
  // (data-show-for) inside the active operation section, and a block marked
  // data-choice-required must have one of its radios picked. The team
  // targets predate this and keep their names — team reassignment also
  // syncs primary checkboxes, which is its own affair.
  static targets = ["operationSelect", "operationSection", "submitButton",
                    "valueInput", "summary",
                    "teamActionRadio", "teamUserBlock",
                    "membershipRadio", "addRoleSelect", "addPrimaryBlock", "replacePrimaryBlock",
                    "actionRadio", "actionBlock", "choiceRadio"]

  // Roles whose primary tag can be assigned through the tool (mirrors
  // DataManagement::Operations::TeamReassignment::PRIMARY_ROLES).
  static PRIMARY_ROLES = ["client_advisor", "sales_representative"]

  connect() {
    this.operationChanged()
  }

  operationChanged() {
    const operation = this.currentOperation()

    this.operationSectionTargets.forEach(section => {
      const active = section.dataset.operation === operation
      section.classList.toggle("d-none", !active)
      this.setInputsEnabled(section, active)
    })

    // Re-apply the active section's internal state. Both callbacks guard
    // themselves, so they're called unconditionally — skipping them left the
    // action blocks in their server-rendered (all visible) state.
    if (operation === "mass_update") this.valueChanged()
    if (operation === "team_reassignment") this.teamActionChanged()
    this.applyActionBlocks()

    this.updateSubmitState()
  }

  valueChanged() {
    this.valueInputTargets.forEach(input => {
      input.classList.toggle("dm-field-filled", this.isFilled(input))
    })

    this.updateSummary()
    this.updateSubmitState()
  }

  // Plain-English restatement of the pending change, plus the one blocking
  // case: half of a paired field (legal structure ↔ tax filing type).
  updateSummary() {
    if (!this.hasSummaryTarget) return

    const filled = this.filledValueInputs()
    const missingPartner = this.missingPairPartner()

    if (missingPartner) {
      this.summaryTarget.textContent =
        `${missingPartner.label} must be set too — the two are validated together.`
      this.summaryTarget.classList.add("alert-warning")
      this.summaryTarget.classList.remove("alert-info")
      return
    }

    this.summaryTarget.classList.add("alert-info")
    this.summaryTarget.classList.remove("alert-warning")

    if (filled.length === 0) {
      this.summaryTarget.textContent = "Choose at least one field to change."
      return
    }

    const parts = filled.map(input => `${this.labelFor(input)} → ${this.displayValue(input)}`)
    this.summaryTarget.textContent =
      `Will set ${parts.join(", ")} on the selected records. Everything else is left as-is.`
  }

  // Returns the unfilled half of a pair when the other half is set.
  missingPairPartner() {
    const paired = this.valueInputTargets.filter(input => input.dataset.pairedWith)

    for (const input of paired) {
      if (!this.isFilled(input)) continue

      const partner = paired.find(candidate => this.fieldName(candidate) === input.dataset.pairedWith)
      if (partner && !this.isFilled(partner)) {
        return { label: this.labelFor(partner) }
      }
    }
    return null
  }

  filledValueInputs() {
    return this.valueInputTargets.filter(input => this.isFilled(input))
  }

  isFilled(input) {
    return input.value !== null && input.value.trim() !== ""
  }

  // bulk_action[values][industry] => industry
  fieldName(input) {
    return input.name.match(/\[values\]\[(.+?)\]/)?.[1] || input.name
  }

  labelFor(input) {
    const label = input.closest("div")?.querySelector("label")
    return label?.textContent.trim() || this.fieldName(input)
  }

  displayValue(input) {
    if (input.tagName === "SELECT") {
      return input.selectedOptions[0]?.textContent.trim() || input.value
    }
    return input.value.trim()
  }

  teamActionChanged() {
    const action = this.checkedTeamAction()

    this.teamUserBlockTargets.forEach(block => {
      const isActive = block.dataset.showFor.split(" ").includes(action)
      block.classList.toggle("d-none", !isActive)
      this.setInputsEnabled(block, isActive)
    })

    this.syncPrimaryCheckboxes()
    this.updateSubmitState()
  }

  checkedTeamAction() {
    return this.teamActionRadioTargets.find(radio => radio.checked)?.value || ""
  }

  actionChanged() {
    this.applyActionBlocks()
    this.updateSubmitState()
  }

  // Reveal the active section's blocks for the checked action card.
  applyActionBlocks() {
    const section = this.currentSection()
    if (!section) return

    const action = this.checkedActionRadio()

    this.actionBlockTargets.forEach(block => {
      if (!section.contains(block)) return

      const isActive = block.dataset.showFor.split(" ").includes(action)
      block.classList.toggle("d-none", !isActive)
      this.setInputsEnabled(block, isActive)
    })
  }

  checkedActionRadio() {
    const section = this.currentSection()
    if (!section) return ""

    return this.actionRadioTargets.find(radio => section.contains(radio) && radio.checked)?.value || ""
  }

  membershipChanged() {
    this.syncPrimaryCheckboxes()
    this.updateSubmitState()
  }

  requiredInputChanged() {
    this.updateSubmitState()
  }

  addRoleChanged() {
    this.syncPrimaryCheckboxes()
  }

  // The "make primary" checkbox only applies to Client Advisor / Sales
  // Representative. For replace it also hides when the picked membership is
  // already primary (the tag carries over automatically).
  syncPrimaryCheckboxes() {
    const action = this.checkedTeamAction()
    const primaryRoles = this.constructor.PRIMARY_ROLES

    if (this.hasAddPrimaryBlockTarget) {
      const role = this.hasAddRoleSelectTarget ? this.addRoleSelectTarget.value : ""
      this.togglePrimaryBlock(this.addPrimaryBlockTarget, action === "add" && primaryRoles.includes(role))
    }

    if (this.hasReplacePrimaryBlockTarget) {
      const picked = this.membershipRadioTargets.find(radio => radio.checked)
      const show = action === "replace" && !!picked &&
        primaryRoles.includes(picked.dataset.role) && picked.dataset.primary !== "true"
      this.togglePrimaryBlock(this.replacePrimaryBlockTarget, show)
    }
  }

  togglePrimaryBlock(block, show) {
    block.classList.toggle("d-none", !show)
    block.querySelectorAll("input").forEach(input => {
      input.disabled = !show
      if (!show) input.checked = false
    })
  }

  // Preview used to be clickable on an empty form for every operation except
  // mass update, so the first click was a guaranteed 422 round-trip.
  updateSubmitState() {
    if (!this.hasSubmitButtonTarget) return

    this.submitButtonTarget.disabled = !this.operationReady()
  }

  operationReady() {
    switch (this.currentOperation()) {
      case "mass_update":
        return this.filledValueInputs().length > 0 && !this.missingPairPartner()
      case "team_reassignment":
        return this.teamReassignmentReady()
      default:
        return this.sectionHasActionCards() ? this.actionCardsReady() : this.activeRequiredInputsFilled()
    }
  }

  sectionHasActionCards() {
    const section = this.currentSection()
    return !!section && this.actionRadioTargets.some(radio => section.contains(radio))
  }

  actionCardsReady() {
    const action = this.checkedActionRadio()
    if (action === "") return false
    if (!this.requiredChoicesPicked(action)) return false

    return this.activeRequiredInputsFilled()
  }

  // Every visible data-choice-required block must have a radio picked — an
  // empty list (no owners/assignees on the selection to pick from) blocks
  // the same way an unpicked one does.
  requiredChoicesPicked(action) {
    const section = this.currentSection()

    return Array.from(section.querySelectorAll("[data-choice-required]")).every(block => {
      if (!block.dataset.showFor.split(" ").includes(action)) return true

      return this.choiceRadioTargets.some(radio => block.contains(radio) && radio.checked)
    })
  }

  teamReassignmentReady() {
    const action = this.checkedTeamAction()
    if (action === "") return false
    // Add needs a person; remove and replace need a membership picked from
    // the list, and replace additionally needs the incoming person.
    if (action !== "add" && !this.membershipRadioTargets.some(radio => radio.checked)) return false

    return this.activeRequiredInputsFilled()
  }

  activeRequiredInputsFilled() {
    return this.requiredInputs().every(input => this.isFilled(input))
  }

  // Which inputs are required is decided by the chosen action, not by whether
  // some earlier callback has hidden or disabled their block yet. Reading the
  // DOM state instead made the gate depend on ordering: before the toggle ran,
  // the hidden "add" block's empty select blocked Preview, and switching the
  // action and back was the only way to clear it.
  requiredInputs() {
    const section = this.operationSectionTargets.find(
      candidate => candidate.dataset.operation === this.currentOperation()
    )
    if (!section) return []

    const action = this.currentAction()

    return Array.from(section.querySelectorAll("[data-dm-required]")).filter(input => {
      const block = input.closest("[data-show-for]")
      if (!block || action === "") return true

      return block.dataset.showFor.split(" ").includes(action)
    })
  }

  currentOperation() {
    return this.hasOperationSelectTarget ? this.operationSelectTarget.value : ""
  }

  currentSection() {
    return this.operationSectionTargets.find(
      candidate => candidate.dataset.operation === this.currentOperation()
    )
  }

  // The sub-action driving data-show-for blocks, per operation.
  currentAction() {
    return this.currentOperation() === "team_reassignment" ? this.checkedTeamAction() : this.checkedActionRadio()
  }

  setInputsEnabled(container, enabled) {
    container.querySelectorAll("input, select, textarea").forEach(input => {
      // TomSelect's internal control input manages its own state.
      if (input.closest(".ts-wrapper")) return

      input.disabled = !enabled
      // TomSelect only reads the disabled attribute at init — a control
      // revealed later must be flipped through its API or it stays locked.
      if (input.tomselect) {
        if (enabled) {
          input.tomselect.enable()
        } else {
          input.tomselect.disable()
        }
      }
    })
  }
}
